import {
  useState,
  useMemo,
  useEffect,
  useRef,
  useCallback,
  memo,
} from 'react';
import { useTheme } from '@/contexts/ThemeContext';

export interface AdminTabBarProps {
  tabs: { id: string; label: string }[];
  activeTab: string;
  onTabChange: (tabId: string) => void;
}

export const AdminTabBar = memo(function AdminTabBar({ tabs, activeTab, onTabChange }: AdminTabBarProps) {
  const { theme } = useTheme();

  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [visibleTabIds, setVisibleTabIds] = useState<string[]>([]);
  const isCalculatingRef = useRef(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const tabsListRef = useRef<HTMLDivElement>(null);
  const moreBtnRef = useRef<HTMLDivElement>(null);
  const tabWidthCacheRef = useRef<Map<string, number>>(new Map());

  const availableTabIds = useMemo(
    () => tabs.map((tab) => tab.id),
    [tabs]
  );

  const overflowTabIds = useMemo(
    () => availableTabIds.filter((id) => !visibleTabIds.includes(id)),
    [availableTabIds, visibleTabIds]
  );

  const hasOverflowTabs = useMemo(
    () => overflowTabIds.length > 0,
    [overflowTabIds]
  );

  const isTabVisible = useCallback(
    (tabId: string) => visibleTabIds.includes(tabId),
    [visibleTabIds]
  );

  const isTabInOverflow = useCallback(
    (tabId: string) => overflowTabIds.includes(tabId),
    [overflowTabIds]
  );

  const selectTab = useCallback(
    (tabId: string) => {
      onTabChange(tabId);
      setShowMoreMenu(false);
    },
    [onTabChange]
  );

  const toggleMoreMenu = useCallback(() => {
    setShowMoreMenu((prev) => !prev);
  }, []);

  // Close the More menu when clicking outside
  useEffect(() => {
    const handleDocumentClick = (e: Event) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.kst-admin-tabs-more')) {
        setShowMoreMenu(false);
      }
    };

    document.addEventListener('click', handleDocumentClick);
    return () => {
      document.removeEventListener('click', handleDocumentClick);
    };
  }, []);

  const calculateVisibleTabs = useCallback(() => {
    if (isCalculatingRef.current) return;
    isCalculatingRef.current = true;

    const container = containerRef.current;
    const tabsList = tabsListRef.current;

    if (!container || !tabsList) {
      isCalculatingRef.current = false;
      return;
    }

    const tabElements = Array.from(
      tabsList.querySelectorAll('.kst-nav-pill')
    ) as HTMLElement[];
    const gap = 8;
    const buffer = 5;
    const moreBtnWidth = 80; // fixed estimate for "More" button

    // Measure visible tabs and cache their widths; hidden tabs keep cached value
    for (const tab of tabElements) {
      const tabId = tab.getAttribute('data-tab-id');
      if (!tabId) continue;
      const w = tab.offsetWidth;
      if (w > 0) {
        tabWidthCacheRef.current.set(tabId, w);
      }
    }

    // Calculate total width of all tabs using cached widths
    const containerWidth = container.clientWidth;
    let totalWidth = 0;
    let idx = 0;
    for (const tabId of availableTabIds) {
      const w = tabWidthCacheRef.current.get(tabId) || 100; // fallback estimate
      totalWidth += w + (idx > 0 ? gap : 0);
      idx++;
    }

    if (totalWidth <= containerWidth + buffer) {
      // All tabs fit
      setVisibleTabIds([...availableTabIds]);
      isCalculatingRef.current = false;
      return;
    }

    // Not all fit — calculate which ones do, reserving space for "More" button
    const availableWidth = containerWidth - moreBtnWidth;
    totalWidth = 0;
    const newVisibleIds: string[] = [];

    for (const tabId of availableTabIds) {
      const w = tabWidthCacheRef.current.get(tabId) || 100;
      const tabWidth = w + (newVisibleIds.length > 0 ? gap : 0);
      if (totalWidth + tabWidth <= availableWidth) {
        totalWidth += tabWidth;
        newVisibleIds.push(tabId);
      } else {
        break;
      }
    }

    // Always show at least 2 tabs
    if (newVisibleIds.length < 2 && availableTabIds.length >= 2) {
      newVisibleIds.length = 0;
      newVisibleIds.push(availableTabIds[0], availableTabIds[1]);
    }

    setVisibleTabIds(newVisibleIds);
    isCalculatingRef.current = false;
  }, [availableTabIds]);

  // Resize listener with rAF throttling
  useEffect(() => {
    let rafId: number | null = null;
    const throttledResize = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        calculateVisibleTabs();
      });
    };
    window.addEventListener('resize', throttledResize);
    return () => {
      window.removeEventListener('resize', throttledResize);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [calculateVisibleTabs]);

  // Recalculate when tabs change
  useEffect(() => {
    // Show all tabs initially so they can be measured, then calculate overflow
    setVisibleTabIds([...availableTabIds]);
    tabWidthCacheRef.current.clear();
    // Give DOM time to render all tabs before measuring
    const timer = setTimeout(() => calculateVisibleTabs(), 100);
    return () => clearTimeout(timer);
  }, [tabs, availableTabIds, calculateVisibleTabs]);

  return (
    <div className={`kst-admin-tabs ${theme}`}>
      <div className="kst-admin-tabs-container" ref={containerRef}>
        <div className="kst-admin-tabs-list" ref={tabsListRef}>
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={`kst-nav-pill${activeTab === tab.id ? ' kst-nav-pill--active' : ''}`}
              data-tab-id={tab.id}
              style={{ display: isTabVisible(tab.id) ? '' : 'none' }}
              onClick={() => selectTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div
          className="kst-admin-tabs-more"
          ref={moreBtnRef}
          style={{ display: hasOverflowTabs ? '' : 'none' }}
        >
          <button
            type="button"
            className="kst-nav-pill kst-admin-tabs-more-btn"
            onClick={toggleMoreMenu}
          >
            More <i className="fa fa-chevron-down"></i>
          </button>
          <ul
            className="kst-admin-tabs-more-menu"
            style={{ display: showMoreMenu ? '' : 'none' }}
          >
            {tabs.map((tab) => (
              <li
                key={`more-${tab.id}`}
                style={{ display: isTabInOverflow(tab.id) ? '' : 'none' }}
              >
                <a
                  href="#"
                  className={activeTab === tab.id ? 'kst-admin-tabs-more-menu-active' : ''}
                  onClick={(e) => {
                    e.preventDefault();
                    selectTab(tab.id);
                  }}
                >
                  {tab.label}
                </a>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
});

(window as any).AdminTabBarReact = AdminTabBar;
